import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Save, Upload, CheckCircle2, Trash2, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TID } from "../lib/testIds";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [addresses, setAddresses] = useState(user?.saved_addresses || []);
  const [newAddress, setNewAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", { phone, saved_addresses: addresses });
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addAddress = () => {
    if (!newAddress.trim()) return;
    setAddresses((prev) => [...prev, newAddress.trim()]);
    setNewAddress("");
  };
  const removeAddress = (i) => {
    setAddresses((prev) => prev.filter((_, idx) => idx !== i));
  };

  const uploadLicense = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/uploads/license", fd);
      await refresh();
      toast.success("License uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section data-testid={TID.profile.root} className="mx-auto max-w-4xl px-5 sm:px-8 py-12">
      <div className="label-caps text-gold mb-2">Account</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-10">Your profile</h1>

      <div className="grid md:grid-cols-[240px_1fr] gap-10">
        <div className="glass rounded-3xl p-6 text-center h-fit">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full mx-auto object-cover ring-2 ring-gold/40" />
          ) : (
            <div className="w-24 h-24 rounded-full mx-auto brand-mark flex items-center justify-center text-4xl">
              {user?.name?.[0] || "P"}
            </div>
          )}
          <div className="mt-4 font-serif text-xl">{user?.name}</div>
          <div className="text-xs text-white/50">{user?.email}</div>
        </div>

        <div className="space-y-6">
          <div className="glass rounded-3xl p-6 space-y-5">
            <div className="label-caps text-gold">Contact</div>
            <label className="block">
              <div className="label-caps text-white/60 mb-2">Phone</div>
              <input
                data-testid={TID.profile.phone}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (212) 555-0142"
                className="w-full bg-ink-700 border border-white/10 rounded-xl px-4 py-3 focus:border-gold outline-none"
              />
            </label>
          </div>

          <div className="glass rounded-3xl p-6 space-y-5">
            <div className="label-caps text-gold">Saved addresses</div>
            {addresses.length === 0 && <p className="text-sm text-white/50">No addresses saved yet.</p>}
            {addresses.map((a, i) => (
              <div key={i} className="flex items-center gap-3 bg-ink-700 rounded-xl px-4 py-3">
                <span className="flex-1 text-sm">{a}</span>
                <button onClick={() => removeAddress(i)} className="text-white/40 hover:text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input
                data-testid={TID.profile.address}
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="Add an address"
                className="flex-1 bg-ink-700 border border-white/10 rounded-xl px-4 py-3 focus:border-gold outline-none"
              />
              <button
                data-testid={TID.profile.addAddress}
                onClick={addAddress}
                className="btn-outline-electric px-4 py-2 rounded-full text-xs inline-flex items-center gap-1 hover:text-white"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>

          <div className="glass rounded-3xl p-6 space-y-5">
            <div className="label-caps text-gold">Driver's license</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => uploadLicense(e.target.files?.[0])}
              className="hidden"
            />
            {user?.license_file_id ? (
              <div className="text-sm text-electric flex items-center gap-2">
                <CheckCircle2 size={14} /> License on file
              </div>
            ) : (
              <div className="text-sm text-white/50">
                Required for self-drive rentals.
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-outline-electric px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 hover:text-white"
            >
              <Upload size={14} /> {uploading ? "Uploading…" : user?.license_file_id ? "Replace" : "Upload license"}
            </button>
          </div>

          <button
            data-testid={TID.profile.save}
            onClick={save}
            disabled={saving}
            className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </section>
  );
}
